/**
 * API Service
 * 
 * Core HTTP client for making API requests.
 * Handles authentication, error handling, and request/response interceptors.
 */

import { API_URL, DEFAULT_HEADERS, REQUEST_TIMEOUT, STORAGE_KEYS, HTTP_STATUS } from '../config/api.config';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

class ApiService {
  private baseURL: string;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.baseURL = API_URL;
  }

  /**
   * Get authentication token from storage
   */
  private getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token from storage
   */
  private getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get default headers with authentication
   */
  private getHeaders(customHeaders?: Record<string, string>): HeadersInit {
    const headers: Record<string, string> = { ...DEFAULT_HEADERS };
    
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const activeTenantId = localStorage.getItem('activeTenantId');
    if (activeTenantId) {
      headers['X-Tenant-Id'] = activeTenantId;
    }

    let resolvedBranchId: string | null = null;
    const currentBranchRaw = localStorage.getItem('currentBranch');
    if (currentBranchRaw) {
      try {
        const currentBranch = JSON.parse(currentBranchRaw);
        const currentBranchTenantId = currentBranch?.tenantId || currentBranch?.restaurantId;
        if (currentBranch?.id && (!activeTenantId || !currentBranchTenantId || currentBranchTenantId === activeTenantId)) {
          resolvedBranchId = currentBranch.id;
        }
      } catch {
        // Ignore malformed cached branch JSON.
      }
    }

    if (!resolvedBranchId && activeTenantId) {
      const tenantBranchMapRaw = localStorage.getItem('currentBranchByTenant');
      if (tenantBranchMapRaw) {
        try {
          const tenantBranchMap = JSON.parse(tenantBranchMapRaw);
          const tenantBranch = tenantBranchMap?.[activeTenantId];
          if (tenantBranch?.id) {
            resolvedBranchId = tenantBranch.id;
          }
        } catch {
          // Ignore malformed tenant branch map JSON.
        }
      }
    }

    if (resolvedBranchId) {
      headers['X-Branch-Id'] = resolvedBranchId;
    }

    return { ...headers, ...customHeaders };
  }

  /**
   * Handle API response
   */
  // private async handleResponse<T>(response: Response, originalRequest?: () => Promise<Response>): Promise<ApiResponse<T>> {
  //   const contentType = response.headers.get('content-type');
  //   const isJson = contentType?.includes('application/json');

  //   let data;
  //   if (isJson) {
  //     data = await response.json();
  //   } else {
  //     data = await response.text();
  //   }

  //   if (!response.ok) {
  //     // Handle authentication errors - try to refresh token
  //     if (response.status === HTTP_STATUS.UNAUTHORIZED && originalRequest) {
  //       return this.handleUnauthorized<T>(originalRequest);
  //     }

  //     const error: ApiError = {
  //       message: data?.message || `HTTP ${response.status}: ${response.statusText}`,
  //       status: response.status,
  //       errors: data?.errors,
  //     };

  //     throw error;
  //   }

  //   return {
  //     success: true,
  //     data: data?.data || data,
  //     message: data?.message,
  //   };
  // }

  private async handleResponse<T>(response: Response,originalRequest?: () => Promise<Response>): Promise<ApiResponse<T>> {

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    let data: any;

    try {
      if (isJson) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error("NON-JSON RESPONSE:", text);
        data = text;
      }
    } catch (e) {
      const text = await response.text();
      console.error("JSON PARSE FAILED:", text);
      throw new Error("Invalid JSON response from server");
    }

    // Handle errors
    if (!response.ok) {
      if (response.status === 401 && originalRequest) {
        return this.handleUnauthorized<T>(originalRequest);
      }

      throw {
        message:
          (typeof data === 'object' && data?.message) ||
          `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
        errors: typeof data === 'object' ? data?.errors : undefined,
      };
    }

    // Ensure ONLY plain object is returned
    if (typeof data !== 'object' || data === null) {
      console.error("INVALID DATA FORMAT:", data);
      throw new Error("Invalid API response format");
    }

    return {
      success: true,
      data: data.data ?? data,
      message: data.message,
    };
  }

  /**
   * Handle 401 Unauthorized - Refresh token and retry
   */
  private async handleUnauthorized<T>(originalRequest: () => Promise<Response>): Promise<ApiResponse<T>> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.clearAuth();
      window.location.href = '/login';
      throw {
        message: 'Session expired. Please login again.',
        status: 401,
      } as ApiError;
    }

    // If already refreshing, queue this request
    if (this.isRefreshing) {
      return new Promise<ApiResponse<T>>((resolve, reject) => {
        this.failedQueue.push({
          resolve: (token: string) => {
            originalRequest().then((response) => {
              this.handleResponse<T>(response).then(resolve).catch(reject);
            });
          },
          reject,
        });
      });
    }

    this.isRefreshing = true;

    try {
      // Call refresh endpoint
      const refreshResponse = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify({ refreshToken }),
      });

      if (!refreshResponse.ok) {
        throw new Error('Token refresh failed');
      }

      const refreshData = await refreshResponse.json();
      const newAccessToken = refreshData.accessToken || refreshData.data?.accessToken;

      if (!newAccessToken) {
        throw new Error('No access token in refresh response');
      }

      // Save new token
      this.setToken(newAccessToken);

      // Process queued requests
      this.failedQueue.forEach((promise) => {
        promise.resolve(newAccessToken);
      });
      this.failedQueue = [];

      // Retry original request with new token
      const retryResponse = await originalRequest();
      return this.handleResponse<T>(retryResponse);
    } catch (error) {
      // Refresh failed - clear auth and redirect
      this.failedQueue.forEach((promise) => {
        promise.reject(error);
      });
      this.failedQueue = [];
      this.clearAuth();
      window.location.href = '/login';

      throw {
        message: 'Session expired. Please login again.',
        status: 401,
      } as ApiError;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): never {
    if (error.name === 'AbortError') {
      throw {
        message: 'Request timeout',
        status: 408,
      } as ApiError;
    }

    if (error instanceof Error && !('status' in error)) {
      throw {
        message: error.message || 'Network error. Please check your connection.',
        status: 0,
      } as ApiError;
    }

    throw error;
  }

  /**
   * Make a GET request
   */
  async get<T = any>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(),
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, () => fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(),
        signal: controller.signal,
        ...options,
      }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Make a POST request
   */
  async post<T = any>(endpoint: string, body?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, () => fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Make a PUT request
   */
  async put<T = any>(endpoint: string, body?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, () => fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Make a PATCH request
   */
  async patch<T = any>(endpoint: string, body?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, () => fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        ...options,
      }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Make a DELETE request
   */
  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        signal: controller.signal,
        ...options,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response, () => fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        signal: controller.signal,
        ...options,
      }));
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Upload a file
   */
  async upload<T = any>(endpoint: string, file: File, fieldName = 'file'): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append(fieldName, file);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const token = this.getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Clear authentication data
   */
  clearAuth(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  /**
   * Set authentication token
   */
  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Set refresh token
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }
}

// Export singleton instance
export const apiService = new ApiService();